import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * Iubenda site IDs — each language now has its OWN full legal suite
 * (Privacy + Cookie + Terms). Terms URL slug differs per locale — Iubenda
 * uses the localised path (e.g. `termini-e-condizioni` for IT,
 * `conditions-generales` for FR, `condiciones-de-uso` for ES, etc.).
 *
 * Mapping supplied by the client on 13 Feb 2026.
 */
const IUBENDA_LEGAL = {
    it: {
        id: "93284770",
        privacy: "https://www.iubenda.com/privacy-policy/93284770",
        cookie: "https://www.iubenda.com/privacy-policy/93284770/cookie-policy",
        terms: "https://www.iubenda.com/termini-e-condizioni/93284770",
    },
    en: {
        // Default English → US variant
        id: "93369101",
        privacy: "https://www.iubenda.com/privacy-policy/93369101",
        cookie: "https://www.iubenda.com/privacy-policy/93369101/cookie-policy",
        terms: "https://www.iubenda.com/terms-and-conditions/93369101",
    },
    "en-gb": {
        id: "59291688",
        privacy: "https://www.iubenda.com/privacy-policy/59291688",
        cookie: "https://www.iubenda.com/privacy-policy/59291688/cookie-policy",
        terms: "https://www.iubenda.com/terms-and-conditions/59291688",
    },
    es: {
        id: "87768663",
        privacy: "https://www.iubenda.com/privacy-policy/87768663",
        cookie: "https://www.iubenda.com/privacy-policy/87768663/cookie-policy",
        terms: "https://www.iubenda.com/condiciones-de-uso/87768663",
    },
    fr: {
        id: "78494513",
        privacy: "https://www.iubenda.com/privacy-policy/78494513",
        cookie: "https://www.iubenda.com/privacy-policy/78494513/cookie-policy",
        terms: "https://www.iubenda.com/conditions-generales/78494513",
    },
    pt: {
        id: "56099177",
        privacy: "https://www.iubenda.com/privacy-policy/56099177",
        cookie: "https://www.iubenda.com/privacy-policy/56099177/cookie-policy",
        terms: "https://www.iubenda.com/termos-e-condicoes/56099177",
    },
};

const legalFor = (lang) => IUBENDA_LEGAL[lang] || IUBENDA_LEGAL.it;

/**
 * Privacy page — powered by Iubenda (legally certified, auto-updated).
 * The Iubenda loader (iubenda.js) is loaded globally in public/index.html,
 * so .iubenda-embed links open in a native inline modal automatically.
 */
export default function Privacy() {
    const { t, lang } = useI18n();
    const legal = legalFor(lang);
    return (
        <div data-testid="privacy-page" style={{ background: "var(--itly-cream)", minHeight: "100vh" }}>
            <header
                style={{
                    padding: "22px 32px",
                    background: "rgba(247,244,238,0.96)",
                    borderBottom: "1px solid var(--itly-border)",
                    position: "sticky",
                    top: 0,
                    zIndex: 20,
                }}
            >
                <div
                    style={{
                        maxWidth: 1000,
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 20,
                    }}
                >
                    <Link
                        to="/"
                        style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}
                        data-testid="privacy-home-link"
                    >
                        <img
                            src="/assets/itly/logo-turista.png"
                            alt="ITLY"
                            style={{ width: 36, height: 36, borderRadius: 10, background: "#8BAFC7", objectFit: "cover" }}
                        />
                        <span className="itly-wordmark" style={{ fontSize: 22 }}>ITLY</span>
                    </Link>
                    <Link to="/" className="itly-btn-secondary" style={{ padding: "8px 14px", fontSize: 13.5 }} data-testid="privacy-back">
                        {t("priv.back")}
                    </Link>
                </div>
            </header>

            <main
                style={{
                    maxWidth: 760,
                    margin: "0 auto",
                    padding: "72px 32px 96px",
                }}
            >
                <p
                    style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12.5,
                        fontWeight: 700,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "var(--itly-orange-700)",
                        margin: 0,
                    }}
                >
                    {t("priv.badge")}
                </p>
                <h1
                    className="itly-serif"
                    style={{
                        marginTop: 14,
                        marginBottom: 14,
                        fontSize: "clamp(38px, 4.4vw, 56px)",
                        lineHeight: 1.05,
                        color: "var(--itly-fg-strong)",
                        letterSpacing: "-0.022em",
                    }}
                >
                    {t("priv.title")}
                </h1>
                <p
                    style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 15.5,
                        lineHeight: 1.65,
                        color: "var(--itly-muted)",
                        margin: 0,
                    }}
                >
                    {t("priv.intro_a")}
                    <a href="https://www.iubenda.com" target="_blank" rel="noopener noreferrer" style={link}>Iubenda</a>
                    {t("priv.intro_b")}
                </p>

                <div
                    style={{
                        marginTop: 40,
                        display: "grid",
                        gap: 18,
                        gridTemplateColumns: "1fr",
                    }}
                >
                    <PolicyCard
                        title={t("priv.card1.title")}
                        desc={t("priv.card1.desc")}
                        href={legal.privacy}
                        testid="privacy-open-privacy"
                    />
                    <PolicyCard
                        title={t("priv.card2.title")}
                        desc={t("priv.card2.desc")}
                        href={legal.cookie}
                        testid="privacy-open-cookie"
                    />
                    <PolicyCard
                        title={t("priv.card3.title")}
                        desc={t("priv.card3.desc")}
                        href={legal.terms}
                        testid="privacy-open-terms"
                    />
                </div>

                <div
                    data-testid="privacy-mvp-disclaimer"
                    style={{
                        marginTop: 44,
                        padding: "16px 20px",
                        background: "var(--itly-peach-050)",
                        border: "1px solid rgba(232, 113, 76, 0.28)",
                        borderRadius: 12,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: "#5A3324",
                    }}
                >
                    <strong style={{ color: "var(--itly-orange-700)" }}>{t("priv.mvp.strong")}</strong>
                    {t("priv.mvp.body")}
                </div>

                <div
                    style={{
                        marginTop: 40,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 13.5,
                        color: "var(--itly-muted)",
                        lineHeight: 1.6,
                    }}
                >
                    <strong style={{ color: "var(--itly-fg-strong)" }}>{t("priv.controller.label")}</strong>
                    {t("priv.controller.body")}
                    <a href="mailto:info.itly9@gmail.com" style={link}>info.itly9@gmail.com</a>.
                </div>
            </main>
        </div>
    );
}

function PolicyCard({ title, desc, href, testid }) {
    return (
        <a
            href={href}
            className="iubenda-white iubenda-noiframe iubenda-embed"
            title={title}
            data-testid={testid}
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 20,
                padding: "22px 24px",
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderLeft: "3px solid var(--itly-orange-500)",
                borderRadius: 14,
                textDecoration: "none",
                boxShadow: "var(--itly-card-shadow)",
                transition: "transform 220ms ease, box-shadow 220ms ease",
            }}
        >
            <div style={{ flex: 1 }}>
                <div
                    className="itly-serif"
                    style={{
                        fontSize: 22,
                        color: "var(--itly-fg-strong)",
                        letterSpacing: "-0.01em",
                        marginBottom: 6,
                    }}
                >
                    {title}
                </div>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 14, lineHeight: 1.55, color: "var(--itly-muted)" }}>
                    {desc}
                </div>
            </div>
            <span
                aria-hidden="true"
                style={{
                    color: "var(--itly-orange-500)",
                    fontSize: 24,
                    flexShrink: 0,
                }}
            >
                →
            </span>
        </a>
    );
}

const link = {
    color: "var(--itly-fg-strong)",
    textDecoration: "underline",
    textUnderlineOffset: 3,
    textDecorationColor: "var(--itly-orange-500)",
};
